/** Fixed-step simulation, variable-rate render. */
export class GameLoop {
  readonly step = 1 / 60;
  private acc = 0;
  private last = 0;
  private raf = 0;
  fps = 0;
  private frames = 0;
  private fpsT = 0;
  running = false;
  /** render at most this often (0 = every animation frame). 30 on a phone saves the battery; the simulation stays at 60 steps/s. */
  /** (owner, Sep 13: 30 was jerky, 60 and 90 on the menu too) 0 = every screen frame; a cap skips frames of the screen's own rate, so it
   *  is even only where it divides that rate — 30 or 60 on a 60 Hz or 120 Hz screen; 90 on a 120 Hz screen alternates 1 and 2 frames */
  maxFps = 0;
  /** (Oct 2, owner: "on desktop it doesn't feel quite right") the screen's refresh interval (ms), from the animation frames' own spacing */
  vsyncMs = 1000 / 60;
  /** draw on every `pace`-th screen refresh: 1 = every one. A frame whose work (the steps and the drawing) took longer than a refresh on a
   *  120 Hz screen used to land on 1, 2 or 3 refreshes in turn — 61 fps that judders; every second one, evenly, is 60 that does not
   *  (docs/PERF.md, Oct 2). Chosen from the frames' work, never below ~50 fps; a cap is a floor under it, in whole refreshes */
  pace = 1;
  private gapsSeen: number[] = [];
  private works: number[] = [];
  private easyRuns = 0;
  /** frames that threw; the first one is reported, the rest only counted (a broken frame usually repeats) */
  errors = 0;
  /** the browser is told once, so it can reach the bug report */
  onError?: (e: unknown) => void;
  /** (perf, Sep 17) the last frame's simulation steps cost more than a frame each: catch up two at most and let the rest go */
  private behind = false;
  /** simulation time dropped so far because the machine could not keep up (seconds) — the debug panel's "slow motion" figure */
  dropped = 0;
  /** (owner, Sep 25) each drawn frame: ms since the last drawn frame, and the drawing's own ms — the graphics quality's Auto (engine/quality.ts) */
  onFrame?: (interval: number, draw: number) => void;
  private drawnAt = 0;

  constructor(private update: (dt: number) => void, private render: (alpha: number) => void) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    const tick = (now: number) => {
      if (!this.running) return;
      let dt = (now - this.last) / 1000;
      this.last = now;
      if (dt > 0.25) dt = 0.25; // tab was hidden; don't spiral
      this.seeRefresh(dt * 1000);
      this.acc += dt;
      let n = 0;
      try {
        // (perf, Sep 17 — docs/PERF.md, a city core at a phone's rate) eight catch-up steps a frame when a step already costs more
        // than a frame made the next frame longer still: 1.1 s frames at the 99th percentile. Once behind, two steps a frame and
        // the rest of the backlog is dropped — the world runs a little slow for a moment instead of standing still for a second
        const most = this.behind ? 2 : 8, t0 = performance.now();
        while (this.acc >= this.step && n++ < most) { this.update(this.step); this.acc -= this.step; }
        if (n >= most && this.acc >= this.step) { this.dropped += this.acc; this.acc = 0; }
        this.behind = n > 0 && (performance.now() - t0) / n > this.step * 1000 * 0.75;
        // (Oct 2) draw on whole refreshes: `pace` of them since the last drawn frame (counted in time, so a late callback still counts)
        if (this.drawnAt && Math.round((now - this.drawnAt) / this.vsyncMs) < this.pace) { this.raf = requestAnimationFrame(tick); return; }
        const r0 = performance.now();
        this.render(this.acc / this.step);
        const r1 = performance.now();
        if (this.onFrame) this.onFrame(this.drawnAt ? now - this.drawnAt : 0, r1 - r0);
        this.drawnAt = now; this.choosePace(r1 - t0);
      } catch (e) {
        // A frame that threw used to end the game: the next frame was never asked for, so the picture froze and every
        // continuous sound held its last value for ever (owner, Sep 10 — an ambulance, and then one note). The loop
        // carries on instead, and the error is reported once so the cause is not lost.
        this.acc = 0;
        if (this.errors++ === 0) { console.error('frame failed', e); this.onError?.(e); }
      }
      this.frames++;
      this.fpsT += dt;
      if (this.fpsT >= 0.5) { this.fps = this.frames / this.fpsT; this.frames = 0; this.fpsT = 0; }
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }
  stop(): void { this.running = false; cancelAnimationFrame(this.raf); }
  /** the refresh interval: the lower edge of the animation frames' spacing (a skipped or late frame only ever makes a gap longer) */
  private seeRefresh(ms: number): void {
    if (!(ms > 2) || ms > 100) return;
    this.gapsSeen.push(ms); if (this.gapsSeen.length < 60) return;
    const s = this.gapsSeen.sort((a, b) => a - b), v = s[Math.floor(s.length * 0.2)]; this.gapsSeen.length = 0;
    this.vsyncMs = Math.abs(v - this.vsyncMs) > 0.5 ? v : this.vsyncMs + (v - this.vsyncMs) * 0.3;
  }
  /** every 30 drawn frames: the fewest refreshes per frame that the frames' work (90th percentile) fits in with room; up at once, down
   *  only after three easy looks in a row (no flapping); at most what keeps ~50 fps; the cap's refreshes as a floor */
  private choosePace(work: number): void {
    this.works.push(work); if (this.works.length < 30) return;
    const s = this.works.sort((a, b) => a - b), p90 = s[Math.floor(s.length * 0.9)]; this.works.length = 0;
    const v = this.vsyncMs, most = Math.max(1, Math.floor(1000 / v / 50)), floor = this.maxFps > 0 ? Math.max(1, Math.round(1000 / this.maxFps / v)) : 1;
    let need = 1; while (need < most && p90 > v * need * 0.85) need++;
    need = Math.max(need, floor);
    if (need > this.pace) { this.pace = need; this.easyRuns = 0; }
    else if (need < this.pace) { if (++this.easyRuns >= 3 && p90 < v * (this.pace - 1) * 0.7) { this.pace = Math.max(floor, this.pace - 1); this.easyRuns = 0; } }
    else this.easyRuns = 0;
    if (this.pace < floor) this.pace = floor;
  }
}
